import {
  useEffect,
  useState
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "../../access/firestore";

import { db } from "../../firebase";
import { useClinic } from "../../context/ClinicContext";

import { medications as defaultMedications } from "../../data/medications";
import { requestVexaConfirm } from "../../notifications/dialogService";

export default function MedicationsSettings() {

  const { clinicId } = useClinic();

  const [medications, setMedications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [editingMedication, setEditingMedication] =
    useState(null);

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [category, setCategory] =
    useState("Todas");

  const [form, setForm] = useState({
    name: "",
    patientType: "Adulto",
    presentation: "Tabletas",
    doseValue: "",
    doseUnit: "mg",
    category: "Analgésico",
    interval: "Cada 8 horas",
    durationValue: "",
    durationUnit: "días",
    route: "VO",
    warnings: "",
    contraindications: "",
    allergyContraindications: ""
  });

  useEffect(() => {

    if (!clinicId) {
      setMedications(defaultMedications);
      setLoading(false);
      return;
    }

    const medicamentosRef = collection(
      db,
      "clinics",
      clinicId,
      "medicamentosConfiguracion"
    );

    const unsubscribe = onSnapshot(
      medicamentosRef,
      snapshot => {

        const personalizados = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            origen: "custom"
        }));

        const personalizadosPorBase = new Map(
            personalizados
                .filter(m => m.medicamentoBase)
                .map(m => [m.medicamentoBase, m])
        );

        const medicamentosFinales = defaultMedications.map(medicamento => {
            const personalizado = personalizadosPorBase.get(
                medicamento.name
        );

            return personalizado || medicamento;
        });

        const personalizadosNuevos = personalizados.filter(
            medicamento =>
                !medicamento.medicamentoBase
        );

        setMedications([
            ...medicamentosFinales,
            ...personalizadosNuevos
        ]);

        setLoading(false);
      },
      error => {
        console.error(
          "Error cargando medicamentos:",
          error
        );

        setMedications(defaultMedications);
        setLoading(false);
      }
    );

    return unsubscribe;

  }, [clinicId]);

  const abrirNuevoMedicamento = () => {
    setEditingMedication(null);

    setForm({
        name: "",
        patientType: "Adulto",
        presentation: "Tabletas",
        doseValue: "",
        doseUnit: "mg",
        category: "Analgésico",
        interval: "Cada 8 horas",
        durationValue: "",
        durationUnit: "días",
        route: "VO",
        warnings: "",
        contraindications: "",
        allergyContraindications: ""
    });

    setShowForm(true);
  };

  const guardarMedicamento = async () => {
    if (!clinicId) return;

    if (!form.name.trim()) {
      alert("Escribe el nombre del medicamento.");
      return;
    }

    const datosBase =
        editingMedication &&
        editingMedication.origen !== "custom"
            ? { ...editingMedication }
            : {};

    delete datosBase.id;

    const datos = {
        ...datosBase,

        name: form.name.trim(),
        category: form.category,
        patientType: form.patientType,
        presentation: form.presentation,

        doseValue: form.doseValue,
        doseUnit: form.doseUnit,

        adultDose:
            form.doseValue && form.doseUnit
            ? `${form.doseValue} ${form.doseUnit}`
            : "",

        interval: form.interval,

        durationValue: form.durationValue,
        durationUnit: form.durationUnit,

        duration:
            form.durationValue
            ? Number(form.durationValue)
            : "",

        route: form.route,

        adultPresentation:
            form.patientType === "Adulto" && form.presentation
            ? `${form.presentation} ${form.doseValue || ""}${form.doseUnit || ""}`.trim()
            : "",

        pediatricPresentation:
            form.patientType !== "Adulto" && form.presentation
            ? `${form.presentation} ${form.doseValue || ""}${form.doseUnit || ""}`.trim()
            : "",

        warnings: form.warnings
            .split(",")
            .map(item => item.trim())
            .filter(Boolean),

        contraindications: form.contraindications
            .split(",")
            .map(item => item.trim())
            .filter(Boolean),

        allergyContraindications:
            form.allergyContraindications
            .split(",")
            .map(item => item.trim())
            .filter(Boolean),

        origen: "custom",
        fechaActualizacion: serverTimestamp()
    };

    try {
      if (editingMedication) {
        if (editingMedication.origen === "custom") {
            await updateDoc(
            doc(
                db,
                "clinics",
                clinicId,
                "medicamentosConfiguracion",
                editingMedication.id
            ),
            datos
            );
        } else {
            const medicamentoExistente =
            medications.find(
                medicamento =>
                medicamento.origen === "custom" &&
                medicamento.medicamentoBase === editingMedication.name
            );

            if (medicamentoExistente) {
            await updateDoc(
                doc(
                db,
                "clinics",
                clinicId,
                "medicamentosConfiguracion",
                medicamentoExistente.id
                ),
                datos
            );
            } else {
            await addDoc(
                collection(
                db,
                "clinics",
                clinicId,
                "medicamentosConfiguracion"
                ),
                {
                ...datos,
                medicamentoBase: editingMedication.name,
                fechaCreacion: serverTimestamp()
                }
            );
            }
        }
        } else {
        await addDoc(
            collection(
            db,
            "clinics",
            clinicId,
            "medicamentosConfiguracion"
            ),
            {
            ...datos,
            fechaCreacion: serverTimestamp()
            }
        );
      }

      setShowForm(false);
      setEditingMedication(null);
    } catch (error) {
      console.error("Error guardando medicamento:", error);
      alert("No fue posible guardar el medicamento.");
    }
  };

  const abrirEdicionMedicamento = medicamento => {
    setEditingMedication(medicamento);

    const presentacionOriginal =
        medicamento.adultPresentation ||
        medicamento.pediatricPresentation ||
        "";

    const presentacion =
        presentacionOriginal.toLowerCase().includes("cápsula")
        ? "Cápsulas"
        : presentacionOriginal.toLowerCase().includes("jarabe")
            ? "Jarabe"
            : presentacionOriginal.toLowerCase().includes("suspensión")
            ? "Suspensión"
            : presentacionOriginal.toLowerCase().includes("ampolleta")
                ? "Ampolleta"
                : presentacionOriginal.toLowerCase().includes("crema")
                ? "Crema"
                : presentacionOriginal.toLowerCase().includes("gel")
                    ? "Gel"
                    : presentacionOriginal.toLowerCase().includes("enjuague")
                    ? "Enjuague"
                    : "Tabletas";

    const dosisMatch =
        presentacionOriginal.match(/(\d+(?:\.\d+)?)\s*(mg|ml|g|mcg|UI|%)/i);

    const intervalo =
        medicamento.interval === "4h"
        ? "Cada 4 horas"
        : medicamento.interval === "6h"
            ? "Cada 6 horas"
            : medicamento.interval === "8h"
            ? "Cada 8 horas"
            : medicamento.interval === "12h"
                ? "Cada 12 horas"
                : medicamento.interval === "24h"
                ? "Cada 24 horas"
                : medicamento.interval === "Única"
                    ? "Dosis única"
                    : "Cada 8 horas";

    setForm({
        name: medicamento.name || "",

        patientType: "Adulto",

        presentation: presentacion,

        doseValue:
        medicamento.doseValue ||
        dosisMatch?.[1] ||
        "",

        doseUnit:
        medicamento.doseUnit ||
        dosisMatch?.[2]?.toLowerCase() ||
        "mg",

        category:
        medicamento.category ||
        "Analgésico",

        interval: intervalo,

        durationValue:
        medicamento.durationValue ??
        medicamento.duration ??
        "",

        durationUnit:
        medicamento.durationUnit ||
        "días",

        route:
        medicamento.route ||
        medicamento.allowedRoutes?.[0] ||
        "VO",

        warnings:
        (medicamento.warnings || []).join(", "),

        contraindications:
        (medicamento.contraindications || []).join(", "),

        allergyContraindications:
        (medicamento.allergyContraindications || []).join(", ")
    });

    setShowForm(true);
  };

  const eliminarMedicamento = async medicamento => {
    if (medicamento.origen !== "custom") {
      return;
    }

    const confirmar = await requestVexaConfirm(
      `¿Eliminar "${medicamento.name}" del catálogo?`
    );

    if (!confirmar) return;

    try {
      await deleteDoc(
        doc(
          db,
          "clinics",
          clinicId,
          "medicamentosConfiguracion",
          medicamento.id
        )
      );
    } catch (error) {
      console.error(
        "Error eliminando medicamento:",
        error
      );

      alert(
        "No fue posible eliminar el medicamento."
      );
    }
  };

  const categorias = [
    "Todas",
    ...new Set(
      medications
        .map(medication => medication.category)
        .filter(Boolean)
    )
  ];

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14
  };

  const medicamentosFiltrados = medications.filter(
    medication => {
      const coincideNombre =
        medication.name
          ?.toLowerCase()
          .includes(search.toLowerCase());

      const coincideCategoria =
        category === "Todas" ||
        medication.category === category;

      return coincideNombre && coincideCategoria;
    }
  );

  const medicamentosPredeterminados =
    medicamentosFiltrados.filter(
        medicamento =>
        medicamento.origen !== "custom"
  );

  const medicamentosPersonalizados =
    medicamentosFiltrados.filter(
        medicamento =>
        medicamento.origen === "custom"
  );

  const medicamentosActivos = medications.length;
  const categoriasConfiguradas = new Set(
    medications.map(medicamento => medicamento.category).filter(Boolean)
  ).size;

  return (
    <div className="vd-settings-module vd-settings-medications">
      <div className="vd-settings-module-header" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20
      }}>
        <div>
          <p className="vd-settings-module-kicker">Experiencia clínica</p>
          <h2 style={{
            margin: 0,
            marginBottom: 8,
            fontSize: 24,
            fontWeight: 700
          }}>
            Catálogo de medicamentos
          </h2>

          <p style={{
            margin: 0,
            color: "#64748b"
          }}>
            Ten la posología frecuente lista para emitir recetas claras y consistentes desde el expediente.
          </p>
        </div>

        <span className="vd-settings-module-badge"><span className="vd-settings-status-dot is-on" /> Listo para recetas</span>
        <button
          type="button"
          onClick={abrirNuevoMedicamento}
          style={{
            padding: "10px 16px",
            border: "none",
            borderRadius: 8,
            background: "#2563eb",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          + Nuevo medicamento
        </button>
      </div>

      <div className="vd-settings-module-stats">
        <div><strong>{medicamentosActivos}</strong><span>medicamentos disponibles</span></div>
        <div><strong>{medicamentosPersonalizados.length}</strong><span>personalizados</span></div>
        <div><strong>{categoriasConfiguradas}</strong><span>categorías</span></div>
      </div>

      <div className="vd-settings-module-toolbar" style={{
        display: "flex",
        gap: 12,
        marginBottom: 20
      }}>
<input
  value={search}
  onChange={e => setSearch(e.target.value)}
  placeholder="Buscar medicamento..."
  style={{
    flex: 1,
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14
  }}
/>

<select
  value={category}
  onChange={e => setCategory(e.target.value)}
  style={{
    minWidth: 180,
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    background: "#fff",
    fontSize: 14
  }}
>
  {categorias.map(categoria => (
    <option
      key={categoria}
      value={categoria}
    >
      {categoria}
    </option>
  ))}
</select>
</div>

  <div className="vd-settings-two-column-list" style={{
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 24,
  alignItems: "start",
  height: "auto",
  maxHeight: "calc(100vh - 340px)",
  overflow: "hidden"
}}>
  {loading ? (
    <div style={{
      padding: 30,
      color: "#64748b"
    }}>
      Cargando medicamentos...
    </div>
  ) : medicamentosFiltrados.length === 0 ? (
    <div style={{
      padding: 30,
      color: "#64748b",
      textAlign: "center"
    }}>
      No se encontraron medicamentos.
    </div>
    ) : (
      <>
        <div style={{
            height: "100%",
            overflowY: "auto",
            paddingRight: 8
            }}>
            <h3 style={{
            margin: "0 0 12px",
            fontSize: 18,
            fontWeight: 700
            }}>
            Predeterminados
            </h3>

            {medicamentosPredeterminados.map(medicamento => (
      <div
        key={`${medicamento.origen || "default"}-${medicamento.id}`}
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 16,
          background: "#fff"
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10
        }}>
          <div>
            <div style={{
              fontWeight: 700,
              fontSize: 16
            }}>
              {medicamento.name}
            </div>

            <div style={{
              marginTop: 4,
              fontSize: 13,
              color: "#64748b"
            }}>
              {medicamento.category || "Sin categoría"}
            </div>
          </div>
        </div>

        {medicamento.adultPresentation && (
          <div style={{
            fontSize: 13,
            marginBottom: 6
          }}>
            <strong>Presentación:</strong>{" "}
            {medicamento.adultPresentation}
          </div>
        )}

        {medicamento.adultDose && (
          <div style={{
            fontSize: 13,
            marginBottom: 6
          }}>
            <strong>Dosis:</strong>{" "}
            {medicamento.adultDose}
          </div>
        )}

        {medicamento.interval && (
          <div style={{
            fontSize: 13,
            marginBottom: 6
          }}>
            <strong>Intervalo:</strong>{" "}
            {medicamento.interval}
          </div>
        )}

        {medicamento.duration && (
          <div style={{
            fontSize: 13,
            marginBottom: 12
          }}>
            <strong>Duración:</strong>{" "}
            {medicamento.duration}
          </div>
        )}

        <div style={{
            display: "flex",
            gap: 8,
            marginTop: 12
            }}>
            <button
                type="button"
                onClick={() =>
                abrirEdicionMedicamento(medicamento)
                }
                style={{
                flex: 1,
                padding: "8px 10px",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                background: "#fff",
                cursor: "pointer"
                }}
            >
                Editar
            </button>

            {medicamento.origen === "custom" && (
                <button
                type="button"
                onClick={() =>
                    eliminarMedicamento(medicamento)
                }
                style={{
                    padding: "8px 10px",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    background: "#fff",
                    color: "#dc2626",
                    cursor: "pointer"
                }}
                >
                Eliminar
                </button>
            )}
        </div>
      </div>
            ))}
      </div>

      <div style={{
        height: "100%",
        overflowY: "auto",
        paddingRight: 8
        }}>
        <h3 style={{
        margin: "0 0 12px",
        fontSize: 18,
        fontWeight: 700
        }}>
        Personalizados
        </h3>

        {medicamentosPersonalizados.map(medicamento => (
          <div
            key={`${medicamento.origen || "default"}-${medicamento.id}`}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 16,
              background: "#fff"
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              marginBottom: 10
            }}>
              <div>
                <div style={{
                  fontWeight: 700,
                  fontSize: 16
                }}>
                  {medicamento.name}
                </div>

                <div style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: "#64748b"
                }}>
                  {medicamento.category || "Sin categoría"}
                </div>
              </div>
            </div>

            {medicamento.adultPresentation && (
              <div style={{
                fontSize: 13,
                marginBottom: 6
              }}>
                <strong>Presentación:</strong>{" "}
                {medicamento.adultPresentation}
              </div>
            )}

            {medicamento.adultDose && (
              <div style={{
                fontSize: 13,
                marginBottom: 6
              }}>
                <strong>Dosis:</strong>{" "}
                {medicamento.adultDose}
              </div>
            )}

            {medicamento.interval && (
              <div style={{
                fontSize: 13,
                marginBottom: 6
              }}>
                <strong>Intervalo:</strong>{" "}
                {medicamento.interval}
              </div>
            )}

            {medicamento.duration && (
              <div style={{
                fontSize: 13,
                marginBottom: 12
              }}>
                <strong>Duración:</strong>{" "}
                {medicamento.duration}
              </div>
            )}

            <div style={{
              display: "flex",
              gap: 8,
              marginTop: 12
            }}>
              <button
                type="button"
                onClick={() =>
                  abrirEdicionMedicamento(medicamento)
                }
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                Editar
              </button>

              <button
                type="button"
                onClick={() =>
                  eliminarMedicamento(medicamento)
                }
                style={{
                  padding: "8px 10px",
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#dc2626",
                  cursor: "pointer"
                }}
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  )}

  {showForm && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: 20,
      boxSizing: "border-box"
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 720,
        maxHeight: "90vh",
        overflowY: "auto",
        background: "#fff",
        borderRadius: 14,
        padding: 24,
        boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
        boxSizing: "border-box"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 700,
            color: "#111827"
          }}
        >
          {editingMedication
            ? "Editar medicamento"
            : "Nuevo medicamento"}
        </h3>

        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setEditingMedication(null);
          }}
          style={{
            border: "none",
            background: "transparent",
            fontSize: 26,
            color: "#6b7280",
            cursor: "pointer",
            lineHeight: 1,
            padding: 4
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 16
        }}
      >
        <input
          value={form.name}
          onChange={e =>
            setForm({
              ...form,
              name: e.target.value
            })
          }
          placeholder="Nombre del medicamento"
          style={inputStyle}
        />

        <select
            value={form.patientType}
            onChange={e =>
                setForm({
                ...form,
                patientType: e.target.value
                })
            }
            style={inputStyle}
            >
            <option value="Adulto">Adulto</option>
            <option value="Infantil">Infantil</option>
            <option value="Pediátrico">Pediátrico</option>
            </select>

            <select
            value={form.presentation}
            onChange={e =>
                setForm({
                ...form,
                presentation: e.target.value
                })
            }
            style={inputStyle}
            >
            <option value="Tabletas">Tabletas</option>
            <option value="Cápsulas">Cápsulas</option>
            <option value="Jarabe">Jarabe</option>
            <option value="Suspensión">Suspensión</option>
            <option value="Solución">Solución</option>
            <option value="Ampolleta">Ampolleta</option>
            <option value="Crema">Crema</option>
            <option value="Gel">Gel</option>
            <option value="Enjuague">Enjuague</option>
            <option value="Gotas">Gotas</option>
            <option value="Otro">Otro</option>
            </select>

            <div
            style={{
                display: "flex",
                gap: 8
            }}
            >
            <input
                type="number"
                value={form.doseValue}
                onChange={e =>
                setForm({
                    ...form,
                    doseValue: e.target.value
                })
                }
                placeholder="Dosis"
                style={{
                ...inputStyle,
                flex: 1
                }}
            />

            <select
                value={form.doseUnit}
                onChange={e =>
                setForm({
                    ...form,
                    doseUnit: e.target.value
                })
                }
                style={{
                ...inputStyle,
                width: 100
                }}
            >
                <option value="mg">mg</option>
                <option value="ml">ml</option>
                <option value="g">g</option>
                <option value="mcg">mcg</option>
                <option value="UI">UI</option>
                <option value="%">%</option>
            </select>
            </div>

            <select
            value={form.category}
            onChange={e =>
                setForm({
                ...form,
                category: e.target.value
                })
            }
            style={inputStyle}
            >
            <option value="Analgésico">Analgésico</option>
            <option value="AINE">AINE</option>
            <option value="Antibiótico">Antibiótico</option>
            <option value="Corticoide">Corticoide</option>
            <option value="Antiséptico">Antiséptico</option>
            <option value="Antiinflamatorio">Antiinflamatorio</option>
            <option value="Anestésico">Anestésico</option>
            <option value="Emergencia">Emergencia</option>
            <option value="Profilaxis">Profilaxis</option>
            <option value="Otro">Otro</option>
        </select>

        <select
            value={form.interval}
            onChange={e =>
                setForm({
                ...form,
                interval: e.target.value
                })
            }
            style={inputStyle}
            >
            <option value="Cada 4 horas">Cada 4 horas</option>
            <option value="Cada 6 horas">Cada 6 horas</option>
            <option value="Cada 8 horas">Cada 8 horas</option>
            <option value="Cada 12 horas">Cada 12 horas</option>
            <option value="Cada 24 horas">Cada 24 horas</option>
            <option value="Dosis única">Dosis única</option>
            <option value="Según necesidad">Según necesidad</option>
            </select>

            <div
            style={{
                display: "flex",
                gap: 8
            }}
            >
            <input
                type="number"
                min="1"
                value={form.durationValue}
                onChange={e =>
                setForm({
                    ...form,
                    durationValue: e.target.value
                })
                }
                placeholder="Duración"
                style={{
                ...inputStyle,
                flex: 1
                }}
            />

            <select
                value={form.durationUnit}
                onChange={e =>
                setForm({
                    ...form,
                    durationUnit: e.target.value
                })
                }
                style={{
                ...inputStyle,
                width: 100
                }}
            >
                <option value="días">días</option>
                <option value="semanas">semanas</option>
                <option value="meses">meses</option>
            </select>
            </div>

            <select
            value={form.route}
            onChange={e =>
                setForm({
                ...form,
                route: e.target.value
                })
            }
            style={inputStyle}
            >
            <option value="VO">Vía oral (VO)</option>
            <option value="SL">Sublingual (SL)</option>
            <option value="IM">Intramuscular (IM)</option>
            <option value="IV">Intravenosa (IV)</option>
            <option value="SC">Subcutánea (SC)</option>
            <option value="Tópica">Tópica</option>
            <option value="Oftálmica">Oftálmica</option>
            <option value="Ótica">Ótica</option>
            <option value="Nasal">Nasal</option>
            <option value="Bucal">Bucal</option>
            <option value="Enjuague">Enjuague</option>
            <option value="Otra">Otra</option>
        </select>

        <input
          value={form.warnings}
          onChange={e =>
            setForm({
              ...form,
              warnings: e.target.value
            })
          }
          placeholder="Advertencias, separadas por comas"
          style={inputStyle}
        />

        <input
          value={form.contraindications}
          onChange={e =>
            setForm({
              ...form,
              contraindications: e.target.value
            })
          }
          placeholder="Contraindicaciones, separadas por comas"
          style={inputStyle}
        />

        <input
          value={form.allergyContraindications}
          onChange={e =>
            setForm({
              ...form,
              allergyContraindications: e.target.value
            })
          }
          placeholder="Alergias, separadas por comas"
          style={inputStyle}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 24
        }}
      >
        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setEditingMedication(null);
          }}
          style={{
            padding: "9px 16px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            background: "#fff",
            cursor: "pointer"
          }}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={guardarMedicamento}
          style={{
            padding: "9px 16px",
            border: "none",
            borderRadius: 8,
            background: "#2563eb",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          Guardar medicamento
        </button>
            </div>
    </div>
  </div>
  )}
    </div>
    </div>
  );
}
